import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from './ui.js';

export interface PickerOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
}

const TRIGGER =
  'flex h-11 min-w-0 max-w-[15rem] items-center gap-2 rounded-field border border-hairline bg-white px-2.5 text-left text-[13px] font-normal text-navy outline-none transition hover:border-royal/45 focus:border-royal/55 focus:ring-[3px] focus:ring-royal/15 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink';

function Chevron(): ReactNode {
  return (
    <svg
      viewBox="0 0 16 16"
      className="ml-auto size-3.5 shrink-0 text-muted"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

export function Picker({
  label,
  title,
  values,
  options,
  multiple = false,
  placeholder,
  disabled = false,
  onChange,
}: {
  readonly label: string;
  readonly title: string;
  readonly values: readonly string[];
  readonly options: readonly PickerOption[];
  readonly multiple?: boolean;
  readonly placeholder: string;
  readonly disabled?: boolean;
  readonly onChange: (next: readonly string[]) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const chosen = options.filter((option) => values.includes(option.value));

  const summary =
    chosen.length === 0
      ? placeholder
      : chosen.length === 1
        ? (chosen[0]?.label ?? placeholder)
        : `${String(chosen.length)} chosen`;

  const needle = query.trim().toLowerCase();
  const matches =
    needle === ''
      ? options
      : options.filter(
          (option) =>
            option.label.toLowerCase().includes(needle) ||
            (option.hint ?? '').toLowerCase().includes(needle),
        );

  const close = (): void => {
    setOpen(false);
    setQuery('');
  };

  const pick = (value: string): void => {
    if (!multiple) {
      onChange([value]);
      close();
      return;
    }

    onChange(
      values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value],
    );
  };

  return (
    <>
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={() => {
          setOpen(true);
        }}
        className={`${TRIGGER} ${chosen.length > 0 ? 'border-royal/45 text-royal' : ''} ${
          disabled ? 'cursor-not-allowed opacity-50' : ''
        }`}
      >
        <span className="truncate">{summary}</span>
        <Chevron />
      </button>

      <Dialog open={open} onClose={close} className="relative z-50">
        <div className="fixed inset-0 bg-navy-deep/40 backdrop-blur-sm" aria-hidden="true" />

        <div className="fixed inset-0 grid place-items-center p-4">
          <DialogPanel className="flex h-[min(32rem,80vh)] w-full max-w-lg flex-col overflow-hidden rounded-card border border-hairline bg-white shadow-menu dark:border-dark-line dark:bg-dark-panel">
            <div className="shrink-0 border-b border-hairline px-4 py-3 dark:border-dark-line">
              <DialogTitle className="mb-2 text-[15px] font-bold text-navy dark:text-dark-ink">
                {title}
              </DialogTitle>

              <input
                autoFocus
                type="text"
                value={query}
                placeholder="Search…"
                aria-label={`Search ${title.toLowerCase()}`}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                className="h-11 w-full rounded-field border border-[rgb(223,228,235)] bg-white px-3.5 text-sm font-normal text-[rgb(20,32,51)] outline-none placeholder:text-muted/70 focus:border-royal/55 dark:border-dark-line dark:bg-dark-field dark:text-dark-ink"
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {matches.length === 0 ? (
                <p className="px-2.5 py-6 text-center text-[13px] text-muted">Nothing matches</p>
              ) : (
                matches.map((option) => {
                  const on = values.includes(option.value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        pick(option.value);
                      }}
                      className={`flex w-full items-center gap-2.5 rounded-field px-2.5 py-2 text-left transition hover:bg-mist dark:hover:bg-white/8 ${
                        on ? 'text-royal' : 'text-navy dark:text-dark-ink'
                      }`}
                    >
                      {multiple ? (
                        <span
                          aria-hidden="true"
                          className={`grid size-4 shrink-0 place-items-center rounded-[5px] border ${
                            on ? 'border-royal bg-royal text-white' : 'border-hairline dark:border-dark-line'
                          }`}
                        >
                          {on ? (
                            <svg
                              viewBox="0 0 16 16"
                              className="size-3"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3.5 8.5l3 3 6-7" />
                            </svg>
                          ) : null}
                        </span>
                      ) : null}

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-semibold">
                          {option.label}
                        </span>
                        {option.hint === undefined ? null : (
                          <span className="block truncate text-[12px] text-muted">
                            {option.hint}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2 border-t border-hairline px-4 py-3 dark:border-dark-line">
              <span className="text-[12.5px] text-muted">
                {multiple ? `${String(values.length)} chosen` : ''}
              </span>
              <Button type="button" size="sm" className="ml-auto" onClick={close}>
                Done
              </Button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
